import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductCompareChecked } from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CompareActive from "@tIntermatic/Icons/CompareActive";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    productSettings: getSettingsCollection(state).productSettings,
    currentLanguage: state.context.session.language,
    currentWebsiteName: state.context.website.name,
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    extendedStyles?: ProductCarouselProductFullStyles;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
    removeProduct?: (productId: string) => void;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductFullStyles {
    container?: GridContainerProps;
    imageGridItem?: GridItemProps;
    productImageStyles?: ProductImageStyles;
    brandGridItem?: GridItemProps;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    descriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    partNumberGridItem?: GridItemProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    unitOfMeasuresGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityBreakPricingGridItem?: GridItemProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    quantityOrderedGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    productStandardActionsContainer?: GridContainerProps;
    addToCartGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    addToListGridItem?: GridItemProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    compareCheckbox?: CheckboxPresentationProps;
    qtyGridItem?: GridItemProps;
    addToCartContainer?: GridContainerProps;
    addToCartButtonGridItem?: GridItemProps;
    productPriceWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    defaultProductImage?: InjectableCss;
    productSaveWrapper?: InjectableCss;
    priceWrapper?: InjectableCss;
    nonPurshsedStyles?: InjectableCss;
    lowStockStyles?: InjectableCss;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const productCarouselProductFullStyles: ProductCarouselProductFullStyles = {
    container: {
        gap: 8,
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    imageGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            height: 330px;
            width: 218px;
            display: flex;
            align-items: center;
            @media all and (max-width: 991px) and (min-width: 0px) {
                height: 200px;
                padding-top: 13px;
            }
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                justify-content: center;
            `,
        },
        image: {
            css: css`
                > img {
                    @media all and (max-width: 992px) and (min-width: 0px) {
                        height: 174px;
                        object-fit: contain;
                    }
                }
            `,
        },
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            margin: 17px 0;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
                span[class^="TypographyStyle"] {
                    font-size: 16px;
                    font-weight: 500;
                    letter-spacing: 0;
                    line-height: 19px;
                    color: #33333a;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    @media all and (max-width: 768px) and (min-width: 0px) {
                        font-size: 14px;
                        line-height: 17px;
                    }
                }
            `,
        },
    },
    partNumberGridItem: {
        width: 12,
        css: css`
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 17px;
            color: #006bc0;
            @media all and (max-width: 768px) and (min-width: 0px) {
                font-size: 12px;
                line-height: 14px;
            }
        `,
    },
    productAvailabilityStyles: {
        container: {
            css: css`
                padding-bottom: 10px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            span[class^="IconWrapper"] {
                                margin-right: 5px;
                            }
                            span[data-test-selector^="availabilityMessage"] {
                                font-size: 12px;
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
            `,
        },
    },
    productStandardActionsContainer: {
        gap: 6,
        css: css`
            padding: 8px 0 20px 0;
        `,
    },
    addToListGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },

    compareCheckbox: {
        iconProps: { src: CompareActive },
        css: css`
            @media all and (max-width: 992px) and (min-width: 0px) {
                display: none;
            }
        `,
    },
    priceGridItem: {
        width: 12,
        css: css`
            margin-bottom: 10px !important;
        `,
    },
    qtyGridItem: {
        width: 5,
        css: css`
            padding-right: 0px;
            margin-bottom: 10px !important;
            flex-basis: 30%;
            max-width: 30%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                        padding-bottom: 5px;
                    `,
                    css`
                        flex-basis: 50%;
                        max-width: 50%;
                        padding-bottom: 5px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    addToCartButtonGridItem: {
        width: 7,
        css: css`
            padding-right: 0px;
            margin-bottom: 10px !important;
            flex-basis: 70%;
            max-width: 70%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                        padding-top: 0px;
                    `,
                    css`
                        button {
                            width: 92%;
                        }
                        flex-basis: 100%;
                        max-width: 100%;
                        padding-top: 0px;
                    `,
                    css`
                        padding-left: 6px;
                    `,
                    css`
                        padding-left: 10px;
                    `,
                    null,
                ])}
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            span {
                font-size: 12px;
                font-weight: bold;
                letter-spacing: 2px;
                line-height: normal;
                text-align: center;
                color: #ffffff;
                text-transform: uppercase;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            letter-spacing: 0px;
                            font-weight: 400;
                        `,
                        css`
                            letter-spacing: 2px;
                        `,
                        css`
                            letter-spacing: 1px;
                        `,
                        css`
                            letter-spacing: 1px;
                        `,
                        null,
                    ])}
            }
        `,
    },
    nonPurshsedStyles: {
        css: css`
            padding: 15px 0px;
            span {
                font-size: 16px;
                font-weight: 600;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                font-size: 14px;
                            `,
                        ],
                        "max",
                    )}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            span {
                                font-size: 14px;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    lowStockStyles: {
        css: css`
            padding-bottom: 10px;
            span {
                font-size: 15px;
                font-weight: 400;
                color: rgb(230, 78, 37);
            }
        `,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProductFull = ({
    carouselId,
    product,
    extendedStyles,
    productInfo,
    productSettings,
    updateProductInfo,
    actionsTemplate,
    removeProduct,
    currentLanguage,
    currentWebsiteName,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductFullStyles, extendedStyles));
    const [isCheckBoxChecked, setIsCheckBoxChecked] = useState(true);

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (!value && removeProduct) {
            removeProduct(productContext.product.id);
        }
    };

    const discontinuedProduct = product.properties?.isObsolete?.toLowerCase() === "true";

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    let lowStockInventory;
    if (
        productInfo.inventory?.qtyOnHand &&
        productInfo.inventory.qtyOnHand > 0 &&
        productInfo.inventory?.qtyOnHand < productInfo.qtyOrdered
    ) {
        lowStockInventory = true;
    }
    return (
        <>
            <ProductContext.Provider value={productContext}>
                <GridContainer
                    id={`productContainer_${product.id}`}
                    {...styles.container}
                    data-test-selector="productContainer"
                >
                    <GridItem {...styles.imageGridItem}>
                        {!showOverlayBadge && (
                            <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                        )}
                        {showOverlayBadge && overlayBadge && (
                            <StyledWrapper
                                {...styles.badgeAreaWrapper}
                                ref={badgeAreaWrapperRef}
                                data-test-selector="productCompareBadge"
                            >
                                <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                                <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                    <Badge
                                        type={overlayBadge.badgeType}
                                        value={
                                            overlayBadge.badgeType === "Image"
                                                ? overlayBadge.largeImageBadgePath
                                                : overlayBadge.displayText
                                        }
                                        badgeTextColor={overlayBadge.textColorHexCode}
                                        badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                        badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                        imageAltText={
                                            overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                        }
                                        textBadgeCss={styles.badgeText}
                                        imageBadgeProps={styles.badgeImageProps}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </GridItem>
                    <GridItem {...styles.descriptionGridItem}>
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    </GridItem>
                    <GridItem {...styles.partNumberGridItem}>
                        <StyledWrapper>
                            {translate("Model#")} {product.productNumber}
                        </StyledWrapper>
                    </GridItem>
                    {showTextBadges && textBadges && (
                        <GridItem {...styles.textBadgeGridItem}>
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />{" "}
                        </GridItem>
                    )}
                    {showImageBadges && imageBadges && (
                        <GridItem {...styles.imageBadgeGridItem}>
                            <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                        </GridItem>
                    )}
                </GridContainer>
                <GridContainer {...styles.productStandardActionsContainer}>
                    <GridItem {...styles.addToListGridItem}>
                        <Checkbox {...styles.compareCheckbox} checked={isCheckBoxChecked} onChange={handleChanged}>
                            {translate("Compare")}
                        </Checkbox>
                    </GridItem>
                </GridContainer>
                {currentWebsiteName.toLowerCase() === "main" &&
                    currentLanguage?.languageCode.toLowerCase() === "en" &&
                    !productContext.product.cantBuy &&
                    !discontinuedProduct && (
                        <GridContainer {...styles.priceWrapper}>
                            <GridItem {...styles.priceGridItem}>
                                <ProductPrice
                                    showLabel={false}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    product={productContext}
                                    extendedStyles={styles.productPriceStyles}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                {currentWebsiteName.toLowerCase() === "main" &&
                    currentLanguage?.languageCode.toLowerCase() === "en" &&
                    !productContext.product.cantBuy &&
                    !discontinuedProduct && (
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    )}
                {currentWebsiteName.toLowerCase() === "main" &&
                    currentLanguage?.languageCode.toLowerCase() === "en" &&
                    !productContext.product.cantBuy &&
                    lowStockInventory && (
                        <StyledWrapper {...styles.lowStockStyles}>
                            <Typography>{siteMessage("Low_Stock_Message")}</Typography>
                        </StyledWrapper>
                    )}
                {currentWebsiteName.toLowerCase() === "main" &&
                    currentLanguage?.languageCode.toLowerCase() === "en" &&
                    !discontinuedProduct && (
                        <GridContainer {...styles.addToCartContainer}>
                            <GridItem {...styles.qtyGridItem}>
                                <ProductQuantityOrdered
                                    labelOverride=""
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    id={`${carouselId}-qty`}
                                />
                            </GridItem>
                            <GridItem {...styles.addToCartButtonGridItem}>
                                <ProductAddToCartButton
                                    data-test-selector="addToCartBtn"
                                    extendedStyles={styles.productAddToCartButtonStyles}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                {currentWebsiteName.toLowerCase() === "main" &&
                    currentLanguage?.languageCode.toLowerCase() === "en" &&
                    productContext.product.cantBuy && (
                        <StyledWrapper {...styles.nonPurshsedStyles}>
                            <Typography>{siteMessage("Product_Non_Purchasable")}</Typography>
                        </StyledWrapper>
                    )}
                {actionsTemplate ? actionsTemplate(product) : []}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProductFull);
